import type { CommercialTermsSource } from './referrer-network-view';
import { Check, Send } from 'lucide-react';
import { ReferrerType } from '@repo/database/browser';
import type { BadgeSpec } from '@/lib/helpers/badge-spec';
import type { Referrer } from '@/api/referrers';

/**
 * Tipo di referrer: una categoria, non uno stato, coi toni del design
 * della rete commerciale, gli stessi ovunque: affiliato neutro, commerciale
 * (RESELLER) blu informativo, rivenditore (AGENT) viola.
 * Nessun tono di esito (verde/rosso).
 */
export const REFERRER_TYPE_BADGES: Record<ReferrerType, BadgeSpec> = {
  [ReferrerType.AFFILIATE]: {
    variant: 'neutral',
    labelKey: 'referrers.types.AFFILIATE',
  },
  [ReferrerType.RESELLER]: {
    variant: 'info',
    labelKey: 'referrers.types.RESELLER',
  },
  [ReferrerType.AGENT]: {
    variant: 'violet',
    labelKey: 'referrers.types.AGENT',
  },
};

/** Opzioni del tipo (filtro e form), nell'ordine della gerarchia. */
export const REFERRER_TYPE_OPTIONS = [
  ReferrerType.AFFILIATE,
  ReferrerType.RESELLER,
  ReferrerType.AGENT,
] as const;

export type ReferrerAccountState = 'registered' | 'invited' | 'unregistered';

type AccountFields = Pick<Referrer, 'user' | 'invitations'>;

/**
 * Stato dell'accesso al pannello: registrato (account con email verificata),
 * invitato (un invito salvato) o nessuno. Il backend conta gli «inviti in
 * attesa» con la stessa regola (`GET referrers/stats`).
 */
export function resolveReferrerAccountState(
  referrer: AccountFields
): ReferrerAccountState {
  if (referrer.user?.isEmailVerified) return 'registered';
  if (referrer.invitations?.length) return 'invited';
  return 'unregistered';
}

/** Toni e icone del design (`RF_ACC`): verde con spunta, ambra con l'aereo, bordo. */
export const REFERRER_ACCOUNT_BADGES: Record<ReferrerAccountState, BadgeSpec> =
  {
    registered: {
      variant: 'success',
      labelKey: 'referrers.registered',
      icon: Check,
    },
    invited: { variant: 'warning', labelKey: 'referrers.invited', icon: Send },
    unregistered: { variant: 'outline', labelKey: 'referrers.unregistered' },
  };

export function getReferrerAccountBadge(referrer: AccountFields): BadgeSpec {
  return REFERRER_ACCOUNT_BADGES[resolveReferrerAccountState(referrer)];
}

/**
 * Da dove viene la quota del commerciale sui clienti di un rivenditore:
 * accordo specifico (info, come il commerciale) o condizioni standard
 * ereditate (neutro).
 */
export const AGREEMENT_SOURCE_BADGES: Record<CommercialTermsSource, BadgeSpec> =
  {
    specific: { variant: 'info', labelKey: 'referrers.agreements.specific' },
    standard: { variant: 'neutral', labelKey: 'referrers.agreements.inherits' },
  };

/** L'attivazione del cliente: segue il predefinito o ha un'eccezione. */
export const ACTIVATION_SOURCE_BADGES: Record<
  'inherited' | 'custom',
  BadgeSpec
> = {
  inherited: {
    variant: 'neutral',
    labelKey: 'referrers.clients_tab.inherited',
  },
  custom: { variant: 'warning', labelKey: 'referrers.clients_tab.customized' },
};
